import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { get, del } from "@/lib/apiClient";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Star,
  Archive,
  Pause,
  Play,
  MoreHorizontal,
  Search,
  Filter,
  Plus,
  Trash2,
  Send,
  Eye,
  MousePointer,
  MessageSquare,
  CheckCircle2,
  Clock,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Link2,
  Globe,
  Shield,
  Mail,
  ToggleLeft,
  ToggleRight,
  GitBranch,
  Users,
  HelpCircle,
  Download,
} from "lucide-react";
import { useRegisterBreadcrumbTitle } from "@/context/BreadcrumbContext";
import { useAuth } from "@/context/AuthContext";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { toast } from "sonner";
import {
  useSequenceQuery,
  useSequenceAnalyticsQuery,
  useCreateSequenceMutation,
  useUpdateSequenceMutation,
  useActivateSequenceMutation,
  usePauseSequenceMutation,
  useArchiveSequenceMutation,
  useAddStepMutation,
  useUpdateStepMutation,
  useDeleteStepMutation,
  useAddVariantMutation,
  useUpdateVariantMutation,
  useDeleteVariantMutation,
  useStarSequenceMutation,
  useUnstarSequenceMutation,
  type Sequence,
  type SequenceStep,
  type SequenceVariant,
} from "@/hooks/useSequences";
import EmailEditor from "@/components/sequences/EmailEditor";
import SpamCheckModal from "@/components/sequences/SpamCheckModal";
import { API_URL, STORAGE } from "@/_config";
import { extractEmailBody } from "@/lib/utils";
import { useSenderIdentitiesQuery } from "@/hooks/useSenderIdentity";

interface SequenceLead {
  id: string;
  name: string;
  company: string;
  title?: string | null;
  email?: string | null;
  workflows: string[];
  enrollment_status: string;
  current_step: number;
  total_steps: number;
  last_activity: string | null;
}

const enrollmentStatusBadge = (status: string): string => {
  const map: Record<string, string> = {
    enrolled:     'sd-lead-badge-blue',
    completed:    'sd-lead-badge-green',
    unsubscribed: 'sd-lead-badge-amber',
    bounced:      'sd-lead-badge-red',
    pending:      'sd-lead-badge-gray',
  };
  return map[status] ?? 'sd-lead-badge-gray';
};

const enrollmentStatusLabel = (status: string): string => {
  const map: Record<string, string> = {
    enrolled:     'Active',
    completed:    'Completed',
    unsubscribed: 'Unsubscribed',
    bounced:      'Bounced',
    pending:      'Pending',
  };
  return map[status] ?? status;
};

const dateRanges = ["Last 7 days", "Last 2 weeks", "Last 4 weeks"];

const formatRelativeTime = (dateStr: string): string => {
  const diff = Math.max(0, Date.now() - new Date(dateStr).getTime());
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  const remHrs = hrs % 24;
  return remHrs > 0 ? `${days}d ${remHrs}h ago` : `${days}d ago`;
};



const timeOptions: string[] = [];
for (let h = 0; h < 24; h++) {
  const suffix = h < 12 ? "AM" : "PM";
  const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  timeOptions.push(`${hour}:00 ${suffix}`);
  timeOptions.push(`${hour}:30 ${suffix}`);
}

const ALL_STEP_TYPES = [
  { value: "follow_up" as const, label: "Follow Up" },
  { value: "value_add" as const, label: "Value Add" },
  { value: "break_up" as const, label: "Break Up" },
  { value: "custom" as const, label: "Custom" },
];

// Placeholder step shown on /app/sequences/new before the sequence is saved to DB.
// Once the user enters a name and blurs, the real sequence (with a real Introduction step)
// is created and the user is navigated to the real ID — replacing these placeholder IDs.
const PLACEHOLDER_STEP_ID = "__placeholder_step__";
const PLACEHOLDER_VARIANT_ID = "__placeholder_variant__";
const PLACEHOLDER_INTRO_STEP: SequenceStep = {
  id: PLACEHOLDER_STEP_ID,
  sequence_id: "",
  step_number: 1,
  step_type: "follow_up",
  step_label: "Introduction",
  delay_config: null,
  variants: [
    {
      id: PLACEHOLDER_VARIANT_ID,
      step_id: PLACEHOLDER_STEP_ID,
      variant_label: "A",
      subject_line: null,
      email_body: null,
      created_at: "",
      updated_at: "",
    },
  ],
  created_at: "",
  updated_at: "",
};

const bodyHasContent = (html: string): boolean => html.replace(/<[^>]*>/g, "").trim().length > 0;

const statusBadgeStyle = (status: Sequence["status"]): React.CSSProperties => {
  switch (status) {
    case "active":
      return { background: "rgba(72, 187, 120, 0.1)", color: "var(--green)" };
    case "paused":
      return { background: "rgba(237, 137, 54, 0.1)", color: "var(--amber)" };
    case "draft":
      return { background: "var(--gray-100)", color: "var(--gray-500)" };
    case "completed":
      return { background: "var(--sky-light)", color: "var(--navy)" };
    case "archived":
      return { background: "var(--gray-100)", color: "var(--gray-500)" };
    default:
      return { background: "var(--gray-100)", color: "var(--gray-500)" };
  }
};

export default function SequenceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === "new";

  const { user } = useAuth();
  const isTrialExpired =
    user?.planStatus === 'trialing' &&
    !!user?.organization?.trial_ends_at &&
    new Date() > new Date(user.organization.trial_ends_at);
  const isPlanDeactivated = user?.planStatus === 'cancelled' || user?.planStatus === 'unpaid';
  const hasGoldPlusAccess =
    !!(user?.organization?.plan_features?.ai_features) && !isTrialExpired && !isPlanDeactivated;
  const isSubscriptionExpired =
    user?.planStatus === 'active' &&
    !!user?.organization?.subscription_ends_at &&
    new Date() > new Date(user.organization.subscription_ends_at);
  const canAddStep =
    !!(user?.organization?.plan_features?.campaign_features) &&
    !isTrialExpired &&
    !isPlanDeactivated &&
    !isSubscriptionExpired;

  const initialTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(isNew ? "Sequences" : (initialTab ?? "Analytics"));
  const [starred, setStarred] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [dateRange, setDateRange] = useState("Last 7 days");
  const [activityPage, setActivityPage] = useState(1);
  const periodMap: Record<string, string> = { "Last 7 days": "7d", "Last 2 weeks": "14d", "Last 4 weeks": "28d" };

  useEffect(() => { setActivityPage(1); }, [dateRange]);

  const queryClient = useQueryClient();
  const [leadSearch, setLeadSearch] = useState("");
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [leadsPage, setLeadsPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const LEADS_PER_PAGE = 25;

  const { data: analyticsRes, isLoading: analyticsLoading } = useSequenceAnalyticsQuery(
    isNew ? undefined : id,
    { period: periodMap[dateRange], activityPage },
  );
  const analytics = analyticsRes?.data;

  const { data: senderIdentitiesData } = useSenderIdentitiesQuery();
  const senderIdentities = senderIdentitiesData?.data ?? [];

  const { data: leadsQueryData, isLoading: leadsLoading } = useQuery({
    queryKey: ['sequence-leads', id, leadsPage, leadSearch],
    queryFn: () => get<{ leads: SequenceLead[]; total: number; per_page: number; current_page: number }>(
      API_URL.SEQUENCES.LEADS(id!),
      { page: leadsPage, per_page: LEADS_PER_PAGE, search: leadSearch || undefined } as Record<string, unknown>,
    ),
    enabled: !isNew && !!id,
    placeholderData: (prev) => prev,
  });

  const [steps, setSteps] = useState<SequenceStep[]>(() => (isNew ? [PLACEHOLDER_INTRO_STEP] : []));
  const [selectedStepId, setSelectedStepId] = useState<string | null>(() => (isNew ? PLACEHOLDER_STEP_ID : null));
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(() =>
    isNew ? PLACEHOLDER_VARIANT_ID : null,
  );

  const [sequenceName, setSequenceName] = useState("");
  const [sequenceStatus, setSequenceStatus] = useState<Sequence["status"]>("draft");
  const [nameError, setNameError] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [subjectError, setSubjectError] = useState(false);
  const [bodyError, setBodyError] = useState(false);
  const [emailViewMode, setEmailViewMode] = useState<"editor" | "preview">("editor");
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [spamCheckOpen, setSpamCheckOpen] = useState(false);
  const [testEmailInput, setTestEmailInput] = useState('');
  const [selectedSenderId, setSelectedSenderId] = useState('');
  const [testSending, setTestSending] = useState(false);

  const [sendFrom, setSendFrom] = useState("9:00 AM");
  const [sendTo, setSendTo] = useState("5:00 PM");
  const [timezone, setTimezone] = useState("America/New_York (EST)");
  const [activeDays, setActiveDays] = useState(["Mon", "Tue", "Wed", "Thu", "Fri"]);
  const [dailyLimit, setDailyLimit] = useState(50);

  const [replyHandling, setReplyHandling] = useState<"stop" | "continue" | "move">("stop");
  const [blockOnBounce, setBlockOnBounce] = useState(false);
  const [sequenceOnReply, setSequenceOnReply] = useState(true);
  const [unsubscribeLink, setUnsubscribeLink] = useState(true);
  const [footerText, setFooterText] = useState("");
  const [newStepType, setNewStepType] = useState<"follow_up" | "value_add" | "break_up" | "custom">("follow_up");

  // Confirmation dialog state
  const [deleteStepTarget, setDeleteStepTarget] = useState<{ stepId: string; stepLabel: string } | null>(null);
  const [deleteVariantTarget, setDeleteVariantTarget] = useState<{
    stepId: string;
    variantId: string;
    variantLabel: string;
  } | null>(null);

  // Local delay state per step (stepId -> { value, unit })
  const [delayValues, setDelayValues] = useState<Record<string, { value: number; unit: string }>>({});

  // Local variant subject/body state (only saved when user clicks Save)
  const [localSubject, setLocalSubject] = useState("");
  const [localBody, setLocalBody] = useState("");
  const saveStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Queries & mutations
  const sequenceQuery = useSequenceQuery(isNew ? undefined : id);
  const createMutation = useCreateSequenceMutation();
  const updateMutation = useUpdateSequenceMutation();
  const activateMutation = useActivateSequenceMutation();
  const pauseMutation = usePauseSequenceMutation();
  const archiveMutation = useArchiveSequenceMutation();
  const starMutation = useStarSequenceMutation();
  const unstarMutation = useUnstarSequenceMutation();
  const pendingNewStepIdRef = useRef<string | null>(null);
  const addStepMutation = useAddStepMutation();
  const updateStepMutation = useUpdateStepMutation();
  const deleteStepMutation = useDeleteStepMutation();
  const addVariantMutation = useAddVariantMutation();
  const updateVariantMutation = useUpdateVariantMutation();
  const deleteVariantMutation = useDeleteVariantMutation();

  // Load sequence data when fetched
  useEffect(() => {
    const seq = sequenceQuery.data?.data;
    if (!seq) return;
    setSequenceName(seq.name === "Untitled Sequence" ? "" : seq.name);
    setSequenceStatus(seq.status);
    setStarred(seq.starred ?? false);
    const newSteps = seq.steps ?? [];
    setSteps(newSteps);
    // Sync delay state from server (only for steps that aren't being actively edited)
    setDelayValues((prev) => {
      const next = { ...prev };
      newSteps.forEach((s) => {
        if (!(s.id in next)) {
          const amount = s.delay_config?.amount ?? s.delay_config?.value;
          const rawUnit = s.delay_config?.unit;
          next[s.id] = {
            value: typeof amount === "number" ? amount : 3,
            unit:
              typeof rawUnit === "string" ? rawUnit.charAt(0).toUpperCase() + rawUnit.slice(1).toLowerCase() : "Days",
          };
        }
      });
      return next;
    });
    if (newSteps.length > 0) {
      const pendingId = pendingNewStepIdRef.current;
      pendingNewStepIdRef.current = null;
      // If a new step was just added, select it; otherwise preserve existing selection if it still exists
      const targetStep = pendingId ? newSteps.find((s) => s.id === pendingId) : null;
      if (targetStep) {
        setSelectedStepId(targetStep.id);
        const fv = targetStep.variants[0];
        if (fv) {
          setSelectedVariantId(fv.id);
          setLocalSubject(fv.subject_line ?? "");
          setLocalBody(extractEmailBody(fv.email_body ?? ""));
        } else {
          setSelectedVariantId(null);
        }
      } else {
        setSelectedStepId((prev) => {
          const stillExists = newSteps.find((s) => s.id === prev);
          return stillExists ? prev : newSteps[0].id;
        });
        setSelectedVariantId((prev) => {
          const step = newSteps.find((s) => s.variants.some((v) => v.id === prev));
          if (step) return prev;
          const firstStep = newSteps[0];
          return firstStep?.variants[0]?.id ?? null;
        });
      }
    }
    // Options
    if (seq.options) {
      if (seq.options.reply_handling) setReplyHandling(seq.options.reply_handling);
      if (seq.options.block_on_bounce !== undefined) setBlockOnBounce(seq.options.block_on_bounce);
      if (seq.options.sequence_on_reply !== undefined) setSequenceOnReply(seq.options.sequence_on_reply);
      if (seq.options.unsubscribe_link !== undefined) setUnsubscribeLink(seq.options.unsubscribe_link);
      if (seq.options.footer_text) setFooterText(seq.options.footer_text);
    }
  }, [sequenceQuery.data]);

  const currentStep = steps.find((s) => s.id === selectedStepId) ?? null;
  const currentVariantData: SequenceVariant | null =
    currentStep?.variants.find((v) => v.id === selectedVariantId) ?? currentStep?.variants[0] ?? null;

  // Sync local subject/body when variant selection changes; also reset errors
  useEffect(() => {
    if (currentVariantData) {
      setLocalSubject(currentVariantData.subject_line ?? "");
      setLocalBody(extractEmailBody(currentVariantData.email_body ?? ""));
    }
    setSubjectError(false);
    setBodyError(false);
  }, [selectedStepId, selectedVariantId]);

  const handleSubjectChange = (value: string) => {
    setLocalSubject(value);
    if (value.trim()) setSubjectError(false);
  };

  const handleBodyChange = (value: string) => {
    setLocalBody(value);
    if (bodyHasContent(value)) setBodyError(false);
  };

  const handleSaveVariant = useCallback(() => {
    let valid = true;
    if (!localSubject.trim()) {
      setSubjectError(true);
      valid = false;
    }
    if (!bodyHasContent(localBody)) {
      setBodyError(true);
      valid = false;
    }
    if (!valid) return;
    if (!currentStep || !currentVariantData || !id || isNew) {
      if (isNew) toast.error("Please enter the sequence name first to save");
      return;
    }
    setSaveStatus("saving");
    updateVariantMutation.mutate(
      {
        stepId: currentStep.id,
        variantId: currentVariantData.id,
        sequenceId: id,
        subject_line: localSubject,
        email_body: localBody,
      },
      {
        onSuccess: () => {
          setSaveStatus("saved");
          if (saveStatusTimerRef.current) clearTimeout(saveStatusTimerRef.current);
          saveStatusTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2000);
          toast.success("Draft saved");
        },
        onError: () => setSaveStatus("error"),
      },
    );
  }, [localSubject, localBody, currentStep, currentVariantData, id, isNew, updateVariantMutation]);

  // Auto-save sequence name on blur.
  // If isNew (no DB record yet), create the sequence now and navigate to its ID.
  // If already saved, update the name.
  const handleAutoSave = () => {
    if (!sequenceName.trim()) {
      setNameError(true);
      return;
    }
    setNameError(false);
    if (isNew) {
      setSaveStatus("saving");
      createMutation.mutate(
        { name: sequenceName.trim() },
        {
          onSuccess: (res) => {
            setSaveStatus("saved");
            if (saveStatusTimerRef.current) clearTimeout(saveStatusTimerRef.current);
            saveStatusTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2000);
            navigate(`/app/sequences/${res.data.id}`, { replace: true });
          },
          onError: () => {
            setSaveStatus("error");
            toast.error("Failed to save sequence");
          },
        },
      );
      return;
    }
    if (!id) return;
    setSaveStatus("saving");
    updateMutation.mutate(
      { id, data: { name: sequenceName } },
      {
        onSuccess: () => {
          setSaveStatus("saved");
          if (saveStatusTimerRef.current) clearTimeout(saveStatusTimerRef.current);
          saveStatusTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2000);
          toast.success("Sequence name saved");
        },
        onError: () => setSaveStatus("error"),
      },
    );
  };

  const toggleDay = (day: string) => {
    setActiveDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));
  };

  const allLeads: SequenceLead[] = leadsQueryData?.data?.leads ?? [];
  const leadsTotal = leadsQueryData?.data?.total ?? 0;
  const leadsLastPage = Math.ceil(leadsTotal / LEADS_PER_PAGE) || 1;

  const toggleLeadSelection = (leadId: string) => {
    setSelectedLeads((prev) => (prev.includes(leadId) ? prev.filter((lid) => lid !== leadId) : [...prev, leadId]));
  };

  const toggleAllLeads = () => {
    if (selectedLeads.length === allLeads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(allLeads.map((l) => l.id));
    }
  };

  const handleRemoveLeads = async () => {
    if (!id || selectedLeads.length === 0) return;
    try {
      await Promise.all(
        selectedLeads.map((leadId) => del(API_URL.SEQUENCES.LEAD(id!, leadId)))
      );
      toast.success(`${selectedLeads.length} lead(s) removed from sequence`);
      setSelectedLeads([]);
      queryClient.invalidateQueries({ queryKey: ['sequence-leads', id] });
    } catch {
      toast.error("Failed to remove leads");
    }
  };

  async function handleExportAll() {
    if (isExporting || leadsTotal === 0) return;
    setIsExporting(true);
    try {
      const raw = localStorage.getItem(STORAGE);
      const token: string | undefined = raw
        ? (JSON.parse(raw) as any)?.access_token ?? (JSON.parse(raw) as any)?.token
        : undefined;

      const params = new URLSearchParams();
      if (leadSearch) params.set('search', leadSearch);

      const exportUrl = `${API_URL.SEQUENCES.LEADS_EXPORT(id!)}${params.size ? '?' + params.toString() : ''}`;
      const response = await fetch(exportUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) return;

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      const disposition = response.headers.get('Content-Disposition');
      const match = disposition?.match(/filename="?([^"]+)"?/);
      a.download = match?.[1] ?? `sequence-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch {
      // silently ignore
    } finally {
      setIsExporting(false);
    }
  }

  const handleExportLeads = () => {
    const toExport = selectedLeads.length > 0
      ? allLeads.filter((l) => selectedLeads.includes(l.id))
      : allLeads;
    const headers = ["Name", "Company", "Status", "Current Step", "Last Activity"];
    const rows = toExport.map((l) => [
      l.name,
      l.company,
      l.enrollment_status,
      `Step ${l.current_step} of ${l.total_steps}`,
      l.last_activity ?? "—",
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sequence-leads.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };



  const availableStepTypes = ALL_STEP_TYPES;

  const DEFAULT_DELAY = { value: 1, unit: "Hours" };

  const handleAddStep = () => {
    if (!id || isNew) return;
    addStepMutation.mutate(
      { sequenceId: id, step_type: newStepType },
      {
        onSuccess: (res) => {
          if (res?.data?.id) {
            const newId = res.data.id;
            pendingNewStepIdRef.current = newId;
            setDelayValues((prev) => ({ ...prev, [newId]: DEFAULT_DELAY }));
            // Auto-save the default delay immediately
            updateStepMutation.mutate({
              sequenceId: id,
              stepId: newId,
              data: { delay_config: { amount: DEFAULT_DELAY.value, unit: DEFAULT_DELAY.unit.toLowerCase() } },
            });
          }
        },
        onError: () => toast.error("Failed to add step"),
      },
    );
  };

  const handleDeleteStep = () => {
    if (!deleteStepTarget || !id) return;
    deleteStepMutation.mutate(
      { sequenceId: id, stepId: deleteStepTarget.stepId },
      {
        onSuccess: () => {
          toast.success("Step deleted");
          if (selectedStepId === deleteStepTarget.stepId) {
            const remaining = steps.filter((s) => s.id !== deleteStepTarget.stepId);
            const first = remaining[0];
            if (first) {
              setSelectedStepId(first.id);
              const fv = first.variants[0];
              if (fv) {
                setSelectedVariantId(fv.id);
                setLocalSubject(fv.subject_line ?? "");
                setLocalBody(extractEmailBody(fv.email_body ?? ""));
              }
            } else {
              setSelectedStepId(null);
              setSelectedVariantId(null);
            }
          }
          setDeleteStepTarget(null);
        },
        onError: () => {
          toast.error("Failed to delete step");
          setDeleteStepTarget(null);
        },
      },
    );
  };

  const handleDeleteVariant = () => {
    if (!deleteVariantTarget || !id) return;
    deleteVariantMutation.mutate(
      { stepId: deleteVariantTarget.stepId, variantId: deleteVariantTarget.variantId, sequenceId: id },
      {
        onSuccess: () => {
          toast.success("Variant deleted");
          if (selectedVariantId === deleteVariantTarget.variantId) {
            const step = steps.find((s) => s.id === deleteVariantTarget.stepId);
            const firstVariant = step?.variants.find((v) => v.id !== deleteVariantTarget.variantId);
            if (firstVariant) {
              setSelectedVariantId(firstVariant.id);
              setLocalSubject(firstVariant.subject_line ?? "");
              setLocalBody(extractEmailBody(firstVariant.email_body ?? ""));
            }
          }
          setDeleteVariantTarget(null);
        },
        onError: () => {
          toast.error("Failed to delete variant");
          setDeleteVariantTarget(null);
        },
      },
    );
  };

  const handleAddVariant = (stepId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!id) return;
    addVariantMutation.mutate(
      { stepId, sequenceId: id },
      {
        onError: () => toast.error("Failed to add variant"),
      },
    );
  };

  const handleActivate = () => {
    if (!id || isNew) return;
    // Frontend validation
    if (!sequenceName.trim()) {
      setNameError(true);
      toast.error("Sequence name is required");
      return;
    }
    if (steps.length === 0) {
      toast.error("Add at least one step before activating");
      return;
    }
    // Check all steps/variants have subject + body content
    for (const step of steps) {
      for (const variant of step.variants ?? []) {
        const isCurrentVariant = variant.id === currentVariantData?.id;
        const subject = isCurrentVariant ? localSubject : (variant.subject_line ?? "");
        const body    = isCurrentVariant ? localBody    : (variant.email_body    ?? "");
        if (!subject.trim() || !bodyHasContent(body)) {
          toast.error(
            `Step ${step.step_number} — ${variant.variant_label}: subject line and email body are required before activating.`,
          );
          return;
        }
      }
    }
    activateMutation.mutate(id, {
      onSuccess: () => {
        toast.success("Sequence activated!");
        navigate("/app/sequences");
      },
      onError: (err: unknown) => {
        const anyErr = err as { response?: { data?: { error?: { code?: string; details?: string[] } } } };
        const errData = anyErr?.response?.data?.error;
        if (errData?.code === "VALIDATION_ERROR" && errData.details) {
          errData.details.forEach((d: string) => toast.error(d));
        } else {
          toast.error("Failed to activate sequence");
        }
      },
    });
  };

  const handlePause = () => {
    if (!id) return;
    pauseMutation.mutate(id, {
      onSuccess: () => {
        setSequenceStatus("paused");
        toast.success("Sequence paused");
      },
      onError: () => toast.error("Failed to pause sequence"),
    });
  };

  const handleArchive = () => {
    if (!id) return;
    archiveMutation.mutate(id, {
      onSuccess: () => {
        toast.success("Sequence archived");
        navigate("/app/sequences");
      },
      onError: () => toast.error("Failed to archive sequence"),
    });
  };

  const handleToggleStar = () => {
    if (!id || isNew) return;
    const wasStarred = starred;
    setStarred(!wasStarred);
    if (wasStarred) {
      unstarMutation.mutate(id, {
        onError: () => setStarred(true),   // rollback
      });
    } else {
      starMutation.mutate(id, {
        onError: () => setStarred(false),  // rollback
      });
    }
  };

  const handleSaveOptions = () => {
    if (!id) return;
    setSaveStatus("saving");
    updateMutation.mutate(
      {
        id,
        data: {
          options: {
            reply_handling: replyHandling,
            block_on_bounce: blockOnBounce,
            sequence_on_reply: sequenceOnReply,
            unsubscribe_link: unsubscribeLink,
            footer_text: footerText,
          },
        },
      },
      {
        onSuccess: () => {
          setSaveStatus("saved");
          if (saveStatusTimerRef.current) clearTimeout(saveStatusTimerRef.current);
          saveStatusTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2000);
          toast.success("Options saved");
        },
        onError: () => {
          setSaveStatus("error");
          toast.error("Failed to save options");
        },
      },
    );
  };

  const autosaveText = () => {
    switch (saveStatus) {
      case "saving":
        return "Saving\u2026";
      case "saved":
        return "Saved \u2713";
      case "error":
        return "Save failed";
      default:
        return "";
    }
  };

  // Tab list based on isNew
  const visibleTabs = isNew ? ["Sequences", "Options"] : ["Analytics", "Leads", "Sequences", "Options"];

  const displayStatus = sequenceStatus;

  useRegisterBreadcrumbTitle(isNew ? undefined : id, sequenceName || "Edit");

  return (
    <div className="portal-page sd-page">
      <Link to="/app/sequences" className="sd-back-link">
        <ArrowLeft size={16} /> Back to Sequences
      </Link>

      <div className="sd-page-header">
        <div className="sd-header-left">
          <div className="sd-name-row">
            <h2 className="sd-title" style={{ color: sequenceName ? undefined : "var(--gray-400)" }}>
              {sequenceName || "New Sequence"}
            </h2>
            <button className="sd-star-btn" onClick={handleToggleStar}>
              <Star
                size={18}
                fill={starred ? "var(--amber)" : "none"}
                color={starred ? "var(--amber)" : "var(--gray-200)"}
              />
            </button>
            {!isNew && (
              <span className="sd-status-badge" style={statusBadgeStyle(displayStatus)}>
                {displayStatus === "active" && <span className="sd-pulse-dot" />}
                {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
              </span>
            )}
            {isNew && <span className="sd-status-badge sd-status-badge-draft">Draft</span>}
          </div>
        </div>
        <div className="sd-header-right">
          {isNew || displayStatus === "draft" || displayStatus === "paused" ? (
            <button className="btn btn-primary btn-sm" onClick={handleActivate} disabled={activateMutation.isPending}>
              <Send size={14} /> Activate Sequence
            </button>
          ) : displayStatus === "active" ? (
            <button className="btn btn-secondary btn-sm" onClick={handlePause} disabled={pauseMutation.isPending}>
              <Pause size={14} /> Pause
            </button>
          ) : displayStatus === "completed" ? (
            <button className="btn btn-primary btn-sm" onClick={handleActivate} disabled={activateMutation.isPending}>
              <Play size={14} /> Resume
            </button>
          ) : null}
          <div className="sd-more-wrap">
            <button className="btn btn-secondary btn-sm sd-more-btn" onClick={() => setShowMoreMenu((v) => !v)}>
              <MoreHorizontal size={16} />
            </button>
            {showMoreMenu && (
              <>
                <div className="sd-more-backdrop" onClick={() => setShowMoreMenu(false)} />
                <div className="sd-more-menu">
                  <button
                    className="sd-more-menu-item sd-more-menu-item--danger"
                    onClick={() => {
                      setShowMoreMenu(false);
                      setShowArchiveConfirm(true);
                    }}
                  >
                    <Archive size={14} /> Archive
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="sd-tab-bar">
        {visibleTabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`sd-tab ${activeTab === tab ? "sd-tab--active" : ""}`}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === "Analytics" && (
        <div className="sd-analytics">
          <div className="sd-analytics-controls">
            <div className="sd-controls-left">
            </div>
            <div className="sd-date-select-wrap">
              <select value={dateRange} onChange={(e) => setDateRange(e.target.value)} className="sd-date-select">
                {dateRanges.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="sd-select-chevron" />
            </div>
          </div>

          {analyticsLoading ? (
            <div className="sd-loading">Loading analytics…</div>
          ) : (<>
          <div className="sd-kpi-grid">
            {[
              { label: "Sequence Started", value: String(analytics?.enrolled ?? 0), sub: "total leads",   icon: Users,         color: "var(--navy)",  tooltip: "Total number of leads enrolled in this sequence." },
              { label: "Sent",             value: String(analytics?.sent ?? 0),     sub: "total emails",  icon: Send,          color: "var(--sky)",   tooltip: "Total number of emails dispatched in this sequence." },
              { label: "Not Reply Rate",    value: analytics ? `${analytics.open_rate}%` : "—",  sub: analytics ? `${analytics.opened} not replied yet` : "",    icon: Eye,           color: "var(--amber)", tooltip: "Percentage of delivered emails that were read but not yet replied to — indicating actively engaged recipients.\nFormula: (Delivered − Replied) ÷ Delivered × 100" },
              { label: "Viewed Rate",      value: analytics ? `${analytics.view_rate}%` : "—",  sub: analytics ? `${analytics.seen} viewed` : "",    icon: MousePointer,  color: "var(--green)", tooltip: "Percentage of delivered emails that were opened by the recipient.\nFormula: Viewed ÷ Delivered × 100" },
              { label: "Reply Rate",       value: analytics ? `${analytics.reply_rate}%` : "—", sub: analytics ? `${analytics.replied} replies` : "", icon: MessageSquare, color: "var(--sky)",   tooltip: "Percentage of delivered emails that received a direct reply from the recipient.\nFormula: Replied ÷ Delivered × 100" },
              { label: "Opportunities",    value: String(analytics?.replied ?? 0),  sub: "",              icon: CheckCircle2,  color: "var(--green)", tooltip: "Leads who replied to at least one email — considered active opportunities." },
            ].map((kpi) => (
              <Tooltip key={kpi.label}>
                <TooltipTrigger asChild>
                  <div className="card sd-kpi-card" style={{ borderTop: `3px solid ${kpi.color}`, cursor: 'default' }}>
                    <kpi.icon size={20} color={kpi.color} className="sd-kpi-icon" />
                    <p className="sd-kpi-label">{kpi.label}</p>
                    <p className="sd-kpi-value">{kpi.value}</p>
                    {kpi.sub && <p className="sd-kpi-sub">{kpi.sub}</p>}
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[240px] text-center whitespace-pre-line leading-snug">
                  {kpi.tooltip}
                </TooltipContent>
              </Tooltip>
            ))}
          </div>

          <div className="card sd-perf-card">
            <h3 className="sd-section-title">Performance Over Time</h3>
            <div className="sd-chart-container">
              {(() => {
                const ts = analytics?.time_series ?? [];
                const hasData = ts.some((d) => d.sent > 0 || d.opened > 0);
                if (!hasData) {
                  return <p className="sd-no-data">No data yet.</p>;
                }
                const W = 600, H = 180;
                const step = ts.length > 1 ? W / (ts.length - 1) : W;
                const maxSent   = Math.max(1, ...ts.map((d) => d.sent));
                const maxOpened = Math.max(1, ...ts.map((d) => d.opened));
                const sentPts   = ts.map((d, i) => `${i * step},${H - (d.sent   / maxSent)   * H}`).join(" ");
                const openPts   = ts.map((d, i) => `${i * step},${H - (d.opened / maxOpened) * H}`).join(" ");
                return (
                  <>
                    <svg viewBox={`0 0 ${W} ${H}`} className="sd-chart-svg">
                      {[60, 120, 160].map((y) => (
                        <line key={y} x1="0" y1={y} x2={W} y2={y} stroke="var(--gray-100)" strokeWidth="1" />
                      ))}
                      <polyline fill="none" stroke="var(--sky)"   strokeWidth="2.5" points={sentPts} />
                      <polyline fill="none" stroke="var(--amber)" strokeWidth="2.5" strokeDasharray="5,5" points={openPts} />
                    </svg>
                    <div className="sd-chart-legend">
                      <span className="sd-chart-legend-item"><span className="sd-legend-line sd-legend-sky" /> Sent</span>
                      <span className="sd-chart-legend-item"><span className="sd-legend-line sd-legend-amber" /> Opens</span>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          <div className="card sd-step-analytics-card">
            <h3 className="sd-section-title sd-section-title-padded">Step Analytics</h3>
            <div className="sd-table-wrap">
              <table className="sd-table">
                <thead>
                  <tr>
                    <th>Step</th>
                    <th>Variant</th>
                    {[
                      { label: "Sent",      tooltip: "Total number of emails dispatched in this sequence." },
                      { label: "Delivered", tooltip: "Percentage of sent emails that successfully reached recipient inboxes.\nFormula: Delivered ÷ Total Sent × 100" },
                      { label: "Not Replied Yet", tooltip: "Percentage of delivered emails that were read but not yet replied to — indicating actively engaged recipients.\nFormula: (Delivered − Replied) ÷ Delivered × 100" },
                      { label: "Viewed",    tooltip: "Percentage of delivered emails that were opened by the recipient.\nFormula: Viewed ÷ Delivered × 100" },
                      { label: "Replied",   tooltip: "Percentage of delivered emails that received a direct reply from the recipient.\nFormula: Replied ÷ Delivered × 100" },
                      { label: "Bounced",   tooltip: "Percentage of emails that failed to deliver or were flagged as spam. Lower is better.\nFormula: (Bounced + Complained) ÷ Total Sent × 100" },
                    ].map((col) => (
                      <th key={col.label}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'help' }}>
                              {col.label}
                              <HelpCircle size={11} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[240px] text-center whitespace-pre-line leading-snug">
                            {col.tooltip}
                          </TooltipContent>
                        </Tooltip>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(analytics?.step_analytics ?? []).length === 0 ? (
                    <tr><td colSpan={8} className="sd-table-muted" style={{ textAlign: 'center', padding: '2rem' }}>No step data yet.</td></tr>
                  ) : (analytics?.step_analytics ?? []).map((row, i) => (
                    <tr key={i}>
                      <td className="sd-table-step-cell">Step {row.step_number}</td>
                      <td>Variant {row.variant_label}</td>
                      <td>{row.sent}</td>
                      <td>{row.delivered}</td>
                      <td>{row.not_replied_yet ?? 0}{row.not_replied_pct != null ? ` (${row.not_replied_pct})` : ''}</td>
                      <td>{row.seen}</td>
                      <td>{row.replied}</td>
                      <td>{row.bounced}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <h3 className="sd-section-title">Activity Feed</h3>
            <div className="sd-activity-list">
              {(analytics?.activities ?? []).length === 0 ? (
                <p className="sd-table-muted" style={{ padding: '1rem 0', textAlign: 'center' }}>No activity recorded yet.</p>
              ) : (analytics?.activities ?? []).map((act, i) => (
                <div
                  key={i}
                  className="sd-activity-item"
                  style={{ borderBottom: i < (analytics?.activities ?? []).length - 1 ? "1px solid var(--gray-100)" : "none" }}
                >
                  <div className="sd-activity-dot" />
                  <p className="sd-activity-text">
                    {act.type === 'sent'
                      ? <><strong>{act.step_label}</strong>{` sent to `}<strong>{(act.contact_count ?? 0).toLocaleString()} contacts</strong></>
                      : act.type === 'replied'
                      ? <><strong>{act.contact_name ?? 'Unknown'}</strong>{` replied to `}<strong>{act.step_label}</strong></>
                      : act.type === 'opened'
                      ? <><strong>{act.contact_name ?? 'Unknown'}</strong>{` opened `}<strong>{act.step_label}</strong></>
                      : <><strong>{act.contact_name ?? 'Unknown'}</strong>{` bounced`}</>}
                  </p>
                  <span className="sd-activity-time">
                    <Clock size={12} /> {formatRelativeTime(act.occurred_at)}
                  </span>
                </div>
              ))}
            </div>
            {(() => {
              const total = analytics?.activities_total ?? 0;
              const totalPages = Math.ceil(total / 10);
              if (totalPages <= 1) return null;
              return (
                <div className="sd-pagination" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', borderTop: '1px solid var(--gray-100)' }}>
                  <button className="btn btn-secondary btn-sm" disabled={activityPage === 1} onClick={() => setActivityPage(p => p - 1)}>Prev</button>
                  <span style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>{activityPage} / {totalPages}</span>
                  <button className="btn btn-secondary btn-sm" disabled={activityPage >= totalPages} onClick={() => setActivityPage(p => p + 1)}>Next</button>
                </div>
              );
            })()}
          </div>
          </>)}
        </div>
      )}

      {activeTab === "Leads" && (
        <div className="sd-leads">
          <div className="sd-leads-toolbar">
            <div className="sd-leads-search-wrap">
              <Search size={16} className="sd-leads-search-icon" />
              <input
                type="text"
                placeholder="Search leads..."
                value={leadSearch}
                onChange={(e) => { setLeadSearch(e.target.value); setLeadsPage(1); }}
                className="sd-leads-search"
              />
            </div>
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleExportAll}
              disabled={leadsTotal === 0 || isExporting}
              title={leadsTotal === 0 ? 'No leads to export' : 'Export all leads as CSV'}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <Download size={14} />
              {isExporting ? 'Exporting…' : 'Export CSV'}
            </button>
          </div>

          {selectedLeads.length > 0 && (
            <div className="sd-bulk-bar">
              <span className="sd-bulk-count">{selectedLeads.length} selected</span>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowRemoveConfirm(true)}>
                <Trash2 size={14} /> Remove
              </button>
              <button className="btn btn-secondary btn-sm" onClick={handleExportLeads}>Export</button>
            </div>
          )}

          <div className="card sd-leads-card">
            <div className="sd-table-wrap">
              <table className="sd-table">
                <thead>
                  <tr>
                    <th className="sd-checkbox-col">
                      <input
                        type="checkbox"
                        checked={allLeads.length > 0 && selectedLeads.length === allLeads.length}
                        onChange={toggleAllLeads}
                      />
                    </th>
                    <th>#</th>
                    <th>Contact</th>
                    <th>Company</th>
                    <th>Last Activity</th>
                    <th>Workflows</th>
                  </tr>
                </thead>
                <tbody>
                  {leadsLoading && (
                    <tr>
                      <td colSpan={8} className="sd-table-muted" style={{ textAlign: 'center', padding: '2rem' }}>
                        Loading…
                      </td>
                    </tr>
                  )}
                  {!leadsLoading && allLeads.length === 0 && (
                    <tr>
                      <td colSpan={8} className="sd-table-muted" style={{ textAlign: 'center', padding: '2rem' }}>
                        No leads found for this sequence.
                      </td>
                    </tr>
                  )}
                  {allLeads.map((lead: SequenceLead, i: number) => (
                    <tr key={lead.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedLeads.includes(lead.id)}
                          onChange={() => toggleLeadSelection(lead.id)}
                        />
                      </td>
                      <td className="sd-table-muted">{(leadsPage - 1) * LEADS_PER_PAGE + i + 1}</td>
                      <td>
                        <div className="sd-name-cell">
                          <div className="sd-avatar">
                            {lead.name.split(" ").map((n: string) => n[0]).join("")}
                          </div>
                          <span className="sd-name-text">{lead.name}</span>
                        </div>
                      </td>
                      <td className="sd-table-muted">{lead.company}</td>
                      <td className="sd-table-muted sd-table-small">{lead.last_activity ?? '—'}</td>
                      <td className="sd-table-muted sd-table-small">{lead.workflows.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {leadsLastPage > 1 && (
              <div className="ld-pagination">
                <p className="ld-pagination-count">
                  Showing {Math.min((leadsPage - 1) * LEADS_PER_PAGE + 1, leadsTotal)}–{Math.min(leadsPage * LEADS_PER_PAGE, leadsTotal)} of {leadsTotal.toLocaleString()} leads
                </p>
                <div className="ld-pagination-controls">
                  <button className="btn btn-secondary btn-sm ld-pag-arrow" onClick={() => setLeadsPage(p => Math.max(1, p - 1))} disabled={leadsPage === 1}>
                    <ChevronLeft size={16} />
                  </button>
                  {leadsPage > 2 && <button className="ld-pag-btn" onClick={() => setLeadsPage(1)}>1</button>}
                  {leadsPage > 3 && <span className="ld-pag-ellipsis">...</span>}
                  {leadsPage > 1 && <button className="ld-pag-btn" onClick={() => setLeadsPage(leadsPage - 1)}>{leadsPage - 1}</button>}
                  <button className="ld-pag-btn ld-pag-btn--active">{leadsPage}</button>
                  {leadsPage < leadsLastPage && <button className="ld-pag-btn" onClick={() => setLeadsPage(leadsPage + 1)}>{leadsPage + 1}</button>}
                  {leadsPage < leadsLastPage - 2 && <span className="ld-pag-ellipsis">...</span>}
                  {leadsPage < leadsLastPage - 1 && <button className="ld-pag-btn" onClick={() => setLeadsPage(leadsLastPage)}>{leadsLastPage}</button>}
                  <button className="btn btn-secondary btn-sm ld-pag-arrow" onClick={() => setLeadsPage(p => Math.min(leadsLastPage, p + 1))} disabled={leadsPage === leadsLastPage}>
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "Sequences" && (
        <div>
          <div className="sd-name-field">
            <label className="sd-field-label">
              Sequence Name <span className="sd-required">*</span>
            </label>
            <input
              type="text"
              className="sd-subject-input"
              style={{ border: nameError ? "1px solid red" : undefined }}
              placeholder="Enter sequence name..."
              value={sequenceName}
              onChange={(e) => {
                setSequenceName(e.target.value);
                if (e.target.value.trim()) setNameError(false);
              }}
              onBlur={() => {
                if (!sequenceName.trim()) {
                  setNameError(true);
                  return;
                }
                handleAutoSave();
              }}
            />
            {nameError && <p className="sd-field-error">Sequence name is required</p>}
          </div>
          <div className="sd-editor-layout">
            <div className="sd-step-sidebar">
              {(hasGoldPlusAccess ? steps : steps.slice(0, 1)).map((step, stepIndex) => (
                <div key={step.id}>
                  {stepIndex > 0 && (
                    <div className="sd-delay-indicator">
                      <Clock size={14} />
                      <span>Send next message in</span>
                      <input
                        type="number"
                        min={1}
                        className="sd-delay-input"
                        value={delayValues[step.id]?.value ?? 3}
                        onChange={(e) => {
                          const val = Math.max(1, parseInt(e.target.value, 10) || 1);
                          setDelayValues((prev) => ({
                            ...prev,
                            [step.id]: { value: val, unit: prev[step.id]?.unit ?? "Days" },
                          }));
                        }}
                        onBlur={() => {
                          if (!id) return;
                          const { value: val, unit } = delayValues[step.id] ?? { value: 3, unit: "Days" };
                          updateStepMutation.mutate(
                            {
                              sequenceId: id,
                              stepId: step.id,
                              data: { delay_config: { amount: val, unit: unit.toLowerCase() } },
                            },
                            {
                              onSuccess: () => toast.success("Delay saved"),
                              onError: () => toast.error("Failed to save delay"),
                            },
                          );
                        }}
                      />
                      <select
                        className="sd-delay-unit"
                        value={delayValues[step.id]?.unit ?? "Days"}
                        onChange={(e) => {
                          if (!id) return;
                          const unit = e.target.value;
                          const val = delayValues[step.id]?.value ?? 3;
                          setDelayValues((prev) => ({ ...prev, [step.id]: { value: val, unit } }));
                          updateStepMutation.mutate(
                            {
                              sequenceId: id,
                              stepId: step.id,
                              data: { delay_config: { amount: val, unit: unit.toLowerCase() } },
                            },
                            { onError: () => toast.error("Failed to save delay") },
                          );
                        }}
                      >
                        <option>Days</option>
                        <option>Hours</option>
                      </select>
                    </div>
                  )}
                  <div
                    className={`sd-step-card ${selectedStepId === step.id ? "sd-step-card--active" : ""}`}
                    onClick={() => {
                      setSelectedStepId(step.id);
                      const firstVariant = step.variants[0];
                      if (firstVariant) {
                        setSelectedVariantId(firstVariant.id);
                        setLocalSubject(firstVariant.subject_line ?? "");
                        setLocalBody(extractEmailBody(firstVariant.email_body ?? ""));
                      }
                    }}
                  >
                    <div className="sd-step-card-header">
                      <span className="sd-step-label">{step.step_label}</span>
                      <div className="sd-step-actions">
                        {stepIndex > 0 && (
                          <button
                            className="sd-step-delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteStepTarget({ stepId: step.id, stepLabel: step.step_label });
                            }}
                            title="Delete step"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </div>
                    {step.variants.map((v, vIndex) => (
                      <div
                        key={v.id}
                        className={`sd-variant-item ${selectedStepId === step.id && selectedVariantId === v.id ? "sd-variant-item--active" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedStepId(step.id);
                          setSelectedVariantId(v.id);
                          setLocalSubject(v.subject_line ?? "");
                          setLocalBody(extractEmailBody(v.email_body ?? ""));
                        }}
                      >
                        <span className="sd-variant-label">Variant {v.variant_label}</span>
                        <span className="sd-variant-subject">{v.subject_line ?? "(no subject)"}</span>
                        {vIndex > 0 && (
                          <button
                            className="sd-delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteVariantTarget({
                                stepId: step.id,
                                variantId: v.id,
                                variantLabel: v.variant_label,
                              });
                            }}
                            title="Delete variant"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      className="sd-add-variant-btn"
                      onClick={(e) => handleAddVariant(step.id, e)}
                      disabled={addVariantMutation.isPending}
                    >
                      <Plus size={14} /> Add variant
                    </button>
                  </div>
                </div>
              ))}
              {canAddStep && (
                <>
                  <div className="sd-step-type-wrap">
                    <select
                      value={newStepType}
                      onChange={(e) => setNewStepType(e.target.value as typeof newStepType)}
                      className="sd-delay-unit sd-step-type-select"
                    >
                      {availableStepTypes.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button className="sd-add-step-btn" onClick={handleAddStep} disabled={addStepMutation.isPending}>
                    <Plus size={16} /> Add Step
                  </button>
                </>
              )}
            </div>

            <div className="sd-editor-panel">
              <div className="sd-editor-header">
                <h3 className="sd-editor-title">
                  {currentStep
                    ? `${currentStep.step_label} \u2014 Variant ${currentVariantData?.variant_label ?? ""}`
                    : "Select a step"}
                </h3>
                <span className="sd-autosave">
                  {saveStatus === "saved" && <CheckCircle2 size={14} />}
                  {autosaveText()}
                </span>
              </div>
              <div className="sd-editor-body">
                <label className="sd-field-label">
                  Subject Line <span className="sd-required">*</span>
                </label>
                <input
                  type="text"
                  className="sd-subject-input"
                  style={{ border: subjectError ? "1.5px solid #e53e3e" : undefined }}
                  placeholder="Enter subject line..."
                  value={localSubject}
                  onChange={(e) => handleSubjectChange(e.target.value)}
                  disabled={isNew || !currentVariantData}
                />
                {subjectError && <p className="sd-field-error">Subject line is required</p>}
                <div className="sd-body-field">
                  <div className="sd-body-field-header">
                    <label className="sd-field-label" style={{ margin: 0 }}>
                      Email Body (HTML) <span className="sd-required">*</span>
                    </label>
                    <div className="sd-view-toggle">
                      <button
                        type="button"
                        className="sd-view-btn sd-view-btn--active"
                        onClick={() => setEmailViewMode("editor")}
                      >
                        &lt;/&gt; Editor
                      </button>
                      <button
                        type="button"
                        className="sd-view-btn"
                        onClick={async () => {
                          // Pre-select default sender if none chosen yet
                          if (!selectedSenderId && senderIdentities.length > 0) {
                            const def = senderIdentities.find((s) => s.is_default) ?? senderIdentities[0];
                            setSelectedSenderId(def.id);
                          }
                          setPreviewModalOpen(true);
                          if (!localBody) { setPreviewHtml(null); return; }
                          const isFullDoc = /<!DOCTYPE\s+html/i.test(localBody) || /<html[\s>]/i.test(localBody);
                          if (isFullDoc) { setPreviewHtml(localBody); return; }
                          setPreviewLoading(true);
                          try {
                            const raw = localStorage.getItem(STORAGE);
                            const stored = raw ? JSON.parse(raw) : null;
                            const token = stored?.token ?? stored?.access_token ?? "";
                            const res = await fetch(API_URL.SEQUENCES.EMAIL_PREVIEW, {
                              method: "POST",
                              headers: {
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json",
                              },
                              body: JSON.stringify({
                                body: localBody,
                                from_name: "Your Team",
                                footer_text: footerText,
                                unsubscribe_link: unsubscribeLink ? "1" : "0",
                              }),
                            });
                            if (!res.ok) { toast.error("Failed to load email preview."); setPreviewHtml(null); return; }
                            const html = await res.text();
                            setPreviewHtml(html);
                          } catch {
                            setPreviewHtml(null);
                          } finally {
                            setPreviewLoading(false);
                          }
                        }}
                      >
                        &#128065; Preview
                      </button>
                      <button
                        type="button"
                        className="sd-view-btn"
                        onClick={() => setSpamCheckOpen(true)}
                      >
                        &#128737; Spam Check
                      </button>
                    </div>
                  </div>
                  <EmailEditor
                    value={localBody}
                    onChange={handleBodyChange}
                    disabled={isNew || !currentVariantData}
                    saveStatus={saveStatus}
                    hasError={bodyError}
                    onSave={handleSaveVariant}
                  />
                  {bodyError && <p className="sd-field-error">Email body is required</p>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "Schedule" && (
        <div className="sd-schedule">
          <div className="card sd-schedule-card">
            <h3 className="sd-section-title">
              <Clock size={18} /> Send Window
            </h3>
            <div className="sd-schedule-row">
              <div className="sd-schedule-field">
                <label className="sd-field-label">From</label>
                <div className="sd-select-wrap">
                  <select className="sd-select" value={sendFrom} onChange={(e) => setSendFrom(e.target.value)}>
                    {timeOptions.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="sd-select-chevron" />
                </div>
              </div>
              <div className="sd-schedule-field">
                <label className="sd-field-label">To</label>
                <div className="sd-select-wrap">
                  <select className="sd-select" value={sendTo} onChange={(e) => setSendTo(e.target.value)}>
                    {timeOptions.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="sd-select-chevron" />
                </div>
              </div>
            </div>
          </div>

          <div className="card sd-schedule-card">
            <h3 className="sd-section-title">
              <Globe size={18} /> Timezone
            </h3>
            <div className="sd-select-wrap sd-timezone-wrap">
              <select className="sd-select" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                <option>America/New_York (EST)</option>
                <option>America/Chicago (CST)</option>
                <option>America/Denver (MST)</option>
                <option>America/Los_Angeles (PST)</option>
                <option>Europe/London (GMT)</option>
                <option>Europe/Berlin (CET)</option>
              </select>
              <ChevronDown size={14} className="sd-select-chevron" />
            </div>
          </div>

          <div className="card sd-schedule-card">
            <h3 className="sd-section-title">
              <Calendar size={18} /> Active Days
            </h3>
            <div className="sd-days-row">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                <button
                  key={day}
                  className={`sd-day-btn ${activeDays.includes(day) ? "sd-day-btn--active" : ""}`}
                  onClick={() => toggleDay(day)}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>

          <div className="card sd-schedule-card">
            <h3 className="sd-section-title">
              <Send size={18} /> Daily Sending Limit
            </h3>
            <input
              type="number"
              className="sd-number-input"
              value={dailyLimit}
              onChange={(e) => setDailyLimit(parseInt(e.target.value) || 0)}
            />
          </div>

          <button className="btn btn-primary">Save Schedule</button>
        </div>
      )}

      {activeTab === "Options" && (
        <div className="sd-options">
          <div className="card sd-options-card">
            <h3 className="sd-section-title">
              <MessageSquare size={18} /> Reply Handling
            </h3>
            <div className="sd-toggle-row">
              <span>Sequence on Reply</span>
              <button
                className={`sd-toggle ${sequenceOnReply ? "sd-toggle--on" : ""}`}
                onClick={() => setSequenceOnReply(!sequenceOnReply)}
              >
                {sequenceOnReply ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
              </button>
            </div>
          </div>

          <div className="card sd-options-card">
            <h3 className="sd-section-title">
              <Shield size={18} /> Bounce Handling
            </h3>
            <div className="sd-toggle-row">
              <span>Block on Bounce</span>
              <button
                className={`sd-toggle ${blockOnBounce ? "sd-toggle--on" : ""}`}
                onClick={() => setBlockOnBounce(!blockOnBounce)}
              >
                {blockOnBounce ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
              </button>
            </div>
          </div>

          <div className="card sd-options-card">
            <h3 className="sd-section-title">
              <Link2 size={18} /> Unsubscribe
            </h3>
            <div className="sd-toggle-row">
              <span>Include unsubscribe link</span>
              <button
                className={`sd-toggle ${unsubscribeLink ? "sd-toggle--on" : ""}`}
                onClick={() => setUnsubscribeLink(!unsubscribeLink)}
              >
                {unsubscribeLink ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
              </button>
            </div>
          </div>

          <div className="card sd-options-card">
            <h3 className="sd-section-title">Footer Text</h3>
            <textarea
              className="sd-footer-textarea"
              value={footerText}
              onChange={(e) => setFooterText(e.target.value)}
            />
          </div>

          <button className="btn btn-primary btn-sm" style={{ alignSelf: "flex-start" }} onClick={handleSaveOptions} disabled={updateMutation.isPending}>
            Save Options
          </button>
        </div>
      )}

      {/* Delete step confirmation */}
      {deleteStepTarget && (
        <div className="sd-confirm-overlay">
          <div className="card sd-confirm-card">
            <h3 className="sd-confirm-title">Delete Step</h3>
            <p className="sd-confirm-text">
              Are you sure you want to delete &ldquo;{deleteStepTarget.stepLabel}&rdquo;? All variants in this step will
              also be deleted.
            </p>
            <div className="sd-confirm-actions">
              <button className="btn btn-secondary btn-sm" onClick={() => setDeleteStepTarget(null)}>
                Cancel
              </button>
              <button
                className="btn btn-sm sd-confirm-delete-btn"
                onClick={handleDeleteStep}
                disabled={deleteStepMutation.isPending}
              >
                <Trash2 size={14} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete variant confirmation */}
      {deleteVariantTarget && (
        <div className="sd-confirm-overlay">
          <div className="card sd-confirm-card">
            <h3 className="sd-confirm-title">Delete Variant</h3>
            <p className="sd-confirm-text">
              Are you sure you want to delete Variant {deleteVariantTarget.variantLabel}? This cannot be undone.
            </p>
            <div className="sd-confirm-actions">
              <button className="btn btn-secondary btn-sm" onClick={() => setDeleteVariantTarget(null)}>
                Cancel
              </button>
              <button
                className="btn btn-sm sd-confirm-delete-btn"
                onClick={handleDeleteVariant}
                disabled={deleteVariantMutation.isPending}
              >
                <Trash2 size={14} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove leads confirmation */}
      {showRemoveConfirm && (
        <div className="sd-confirm-overlay">
          <div className="card sd-confirm-card">
            <h3 className="sd-confirm-title">Remove {selectedLeads.length} Lead{selectedLeads.length !== 1 ? 's' : ''}</h3>
            <p className="sd-confirm-text">
              Are you sure you want to remove {selectedLeads.length === 1 ? 'this lead' : `these ${selectedLeads.length} leads`} from the sequence? They will no longer receive emails from this sequence.
            </p>
            <div className="sd-confirm-actions">
              <button className="btn btn-secondary btn-sm" onClick={() => setShowRemoveConfirm(false)}>
                Cancel
              </button>
              <button
                className="btn btn-sm sd-confirm-delete-btn"
                onClick={() => {
                  setShowRemoveConfirm(false);
                  handleRemoveLeads();
                }}
              >
                <Trash2 size={14} /> Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive confirmation */}
      {showArchiveConfirm && (
        <div className="sd-confirm-overlay">
          <div className="card sd-confirm-card">
            <h3 className="sd-confirm-title">Archive Sequence</h3>
            <p className="sd-confirm-text">
              Are you sure you want to archive &ldquo;{sequenceName}&rdquo;? It will no longer send emails and will be
              moved to your archive.
            </p>
            <div className="sd-confirm-actions">
              <button className="btn btn-secondary btn-sm" onClick={() => setShowArchiveConfirm(false)}>
                Cancel
              </button>
              <button
                className="btn btn-sm sd-confirm-delete-btn"
                onClick={() => {
                  setShowArchiveConfirm(false);
                  handleArchive();
                }}
                disabled={archiveMutation.isPending}
              >
                <Archive size={14} /> Archive
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Preview + Test Email Modal */}
      {previewModalOpen && (
        <div className="ep-overlay" onClick={() => setPreviewModalOpen(false)}>
          <div className="ep-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ep-modal-header">
              <span className="ep-modal-title">Email Preview</span>
              <button className="ep-close-btn" onClick={() => setPreviewModalOpen(false)}>&#x2715;</button>
            </div>
            <div className="ep-modal-body">
              <div className="ep-left-panel">
                <div className="ep-panel-section">
                  <span className="ep-panel-label">&#x2708; Test Email</span>
                </div>
                <label className="ep-field-label">Send from:</label>
                <select
                  className="ep-select"
                  value={selectedSenderId}
                  onChange={(e) => setSelectedSenderId(e.target.value)}
                >
                  <option value="">Select sender…</option>
                  {senderIdentities.map((s) => (
                    <option key={s.id} value={s.id}>{s.from_name} ({s.from_email})</option>
                  ))}
                </select>
                <label className="ep-field-label" style={{ marginTop: 16 }}>Send to:</label>
                <textarea
                  className="ep-textarea"
                  placeholder="email1@example.com, email2@example.com"
                  value={testEmailInput}
                  onChange={(e) => setTestEmailInput(e.target.value)}
                  rows={4}
                />
                <p className="ep-hint">Separate multiple addresses with commas</p>
                <button
                  className="ep-send-btn"
                  disabled={testSending || !selectedSenderId || !testEmailInput.trim()}
                  onClick={async () => {
                    const toEmails = testEmailInput.split(',').map((e) => e.trim()).filter(Boolean);
                    if (!toEmails.length) { toast.error('Enter at least one email address'); return; }
                    if (!selectedSenderId) { toast.error('Select a sender profile'); return; }
                    setTestSending(true);
                    try {
                      const raw = localStorage.getItem(STORAGE);
                      const stored = raw ? JSON.parse(raw) : null;
                      const token = stored?.token ?? stored?.access_token ?? '';
                      const res = await fetch(API_URL.SEQUENCES.SEND_TEST_EMAIL, {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          body: localBody,
                          subject: localSubject || '(No subject)',
                          sender_identity_id: selectedSenderId,
                          to_emails: toEmails,
                          footer_text: footerText ?? '',
                          unsubscribe_link: unsubscribeLink ? '1' : '0',
                        }),
                      });
                      if (!res.ok) {
                        const err = await res.json().catch(() => null);
                        toast.error(err?.error?.message ?? 'Failed to send test email');
                      } else {
                        toast.success(`Test email sent to ${toEmails.length} address${toEmails.length > 1 ? 'es' : ''}`);
                      }
                    } catch {
                      toast.error('Failed to send test email');
                    } finally {
                      setTestSending(false);
                    }
                  }}
                >
                  {testSending ? 'Sending…' : '✈ Send test email'}
                </button>
              </div>
              <div className="ep-right-panel">
                {previewLoading ? (
                  <div className="ep-preview-loading">Loading preview…</div>
                ) : (
                  <iframe
                    srcDoc={previewHtml ?? "<p style='color:#a0aec0;font-family:Arial,sans-serif;padding:20px;'>No content to preview.</p>"}
                    className="ep-preview-frame"
                    title="Email Preview"
                    sandbox="allow-same-origin"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Spam Checker Modal */}
      <SpamCheckModal
        open={spamCheckOpen}
        onClose={() => setSpamCheckOpen(false)}
        body={localBody}
        subject={localSubject}
        canUseAi={hasGoldPlusAccess}
      />
    </div>
  );
}
